import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { LabelPrintService } from '../../../core/services/label-print.service';
import { PageTitleService } from '../../../core/services/page-title.service';

// This page's own layout-editor data model. Saved/loaded via getLabelLayouts /
// saveLabelLayouts for future use — actual barcode printing now always uses a
// fixed 50mm×25mm ZPL layout (see LabelPrintService) and no longer reads these.
export type FieldOrientation = 'H' | 'VT' | 'VB';
export type FieldLocation    = 'left' | 'right';

export interface LabelLayout {
  name: string;
  pageSize: 'A4' | 'thermal' | 'die-cut';
  printerType: 'browser' | 'zebra';
  labelWidth: number;
  labelHeight: number;
  labelGap: number;
  dpi: 203 | 300;
  showTitle: boolean;
  showAuthor: boolean;
  showLibrary: boolean;
  titleOrientation?:   FieldOrientation;
  authorOrientation?:  FieldOrientation;
  libraryOrientation?: FieldOrientation;
  barcodeOrientation?: FieldOrientation;
  titleLocation?:   FieldLocation;
  authorLocation?:  FieldLocation;
  libraryLocation?: FieldLocation;
  barcodeLocation?: FieldLocation;
  /** Zebra media sensing: Y=Gap/Notch, N=Continuous, M=BlackMark, W=Web/Notch */
  mediaType?: 'Y' | 'N' | 'M' | 'W';
  /** ZPL print darkness 0–30 (^MD command) */
  darkness?: number;
  /** ZPL print speed 2–6 ips (^PR command) */
  printSpeed?: number;
  titleFontSizeMm?: number;
  authorFontSizeMm?: number;
  libraryFontSizeMm?: number;
  srnoFontSizeMm?: number;
  barcodeHeightMm?: number;
  barcodeModuleWidth?: number;
  topPaddingMm?: number;
  isDefault?: boolean;
}

export const LABEL_LAYOUT_DEFAULTS: Omit<LabelLayout, 'name'> = {
  pageSize:    'die-cut',
  printerType: 'zebra',
  labelWidth:  50,
  labelHeight: 25,
  labelGap:    3,
  dpi:         203,
  showTitle:   true,
  showAuthor:  false,
  showLibrary: true,
  titleOrientation:   'H',
  authorOrientation:  'H',
  libraryOrientation: 'H',
  barcodeOrientation: 'H',
  titleLocation:   'left',
  authorLocation:  'left',
  libraryLocation: 'left',
  barcodeLocation: 'left',
  mediaType:  'Y',
  darkness:   10,
  printSpeed: 4,
  titleFontSizeMm:   2.2,
  authorFontSizeMm:  1.8,
  libraryFontSizeMm: 1.8,
  srnoFontSizeMm:    2.0,
  barcodeHeightMm:   0,
  barcodeModuleWidth: 2,
  topPaddingMm:      1.5,
};

const PRESETS: { label: string; cfg: Partial<Omit<LabelLayout, 'name'>> }[] = [
  // ── Browser / HTML presets ──────────────────────────────────────────────
  { label: 'A4 · 72mm',         cfg: { printerType: 'browser', pageSize: 'A4',      labelWidth: 72,  labelHeight: 25, labelGap: 3 } },
  { label: 'A4 · 58mm',         cfg: { printerType: 'browser', pageSize: 'A4',      labelWidth: 58,  labelHeight: 25, labelGap: 3 } },
  { label: 'Thermal · 50mm',    cfg: { printerType: 'browser', pageSize: 'thermal', labelWidth: 50,  labelHeight: 25, labelGap: 0 } },
  { label: 'Thermal · 60mm',    cfg: { printerType: 'browser', pageSize: 'thermal', labelWidth: 60,  labelHeight: 25, labelGap: 0 } },
  { label: 'Die-cut · 50×25',   cfg: { printerType: 'browser', pageSize: 'die-cut', labelWidth: 50,  labelHeight: 25, labelGap: 3 } },
  { label: 'Die-cut · 76×25',   cfg: { printerType: 'browser', pageSize: 'die-cut', labelWidth: 76,  labelHeight: 25, labelGap: 3 } },
  // ── Zebra ZPL presets (ZD230 · 203 DPI) ────────────────────────────────
  { label: 'Zebra · 50×25mm',   cfg: { printerType: 'zebra', pageSize: 'die-cut', dpi: 203, labelWidth: 50,  labelHeight: 25, labelGap: 3, mediaType: 'Y', darkness: 10, printSpeed: 4 } },
  { label: 'Zebra · 38×25mm',   cfg: { printerType: 'zebra', pageSize: 'die-cut', dpi: 203, labelWidth: 38,  labelHeight: 25, labelGap: 3, mediaType: 'Y', darkness: 10, printSpeed: 4 } },
  { label: 'Zebra · 100×50mm',  cfg: { printerType: 'zebra', pageSize: 'die-cut', dpi: 203, labelWidth: 100, labelHeight: 50, labelGap: 3, mediaType: 'Y', darkness: 10, printSpeed: 4 } },
];

@Component({
  selector: 'app-label-config',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './label-config.component.html',
  styleUrl: './label-config.component.scss',
})
export class LabelConfigComponent implements OnInit {
  layouts: LabelLayout[] = [];
  activeIndex = 0;
  isNew = false;
  editing: LabelLayout = { name: '', ...LABEL_LAYOUT_DEFAULTS };
  presets = PRESETS;
  readonly speedOptions = [2, 3, 4, 5, 6];

  markAsDefault = false;

  choosingNewDefault = false;
  newDefaultIdx      = 0;
  pendingDeleteIdx   = -1;

  isLoading = false;
  isSaving  = false;
  errorMessage   = '';
  successMessage = '';
  nameError = '';

  labelNameSource: 'center' | 'licensee' | 'custom' = 'center';
  labelNameCustomText = '';

  private savedSnapshot = '';

  get isDirty(): boolean {
    if (this.isNew) return true;
    return JSON.stringify(this.editing) !== this.savedSnapshot;
  }

  get canDelete(): boolean {
    return !this.isNew && this.layouts.length > 1;
  }

  get isCurrentDefault(): boolean {
    if (this.isNew) return false;
    return this.layouts[this.activeIndex]?.isDefault === true;
  }

  get remainingLayouts(): LabelLayout[] {
    return this.layouts.filter((_, i) => i !== this.pendingDeleteIdx);
  }

  get previewPx(): number {
    return Math.min(260, Math.max(60, this.editing.labelWidth * 2.4));
  }

  get previewScale(): number {
    return this.previewPx / this.editing.labelWidth;
  }

  /** Convert a mm value to preview pixels, falling back to def if undefined. */
  lpx(mm: number | undefined, def: number): number {
    return Math.max(4, Math.round((mm ?? def) * this.previewScale * 10) / 10);
  }

  get previewBarcodePx(): number {
    const scale = this.previewScale;
    if (this.editing.barcodeHeightMm && this.editing.barcodeHeightMm > 0) {
      return Math.max(6, Math.round(this.editing.barcodeHeightMm * scale));
    }
    const lh = this.editing.labelHeight || 25;
    return Math.max(6, Math.round(Math.max(5, lh * 0.45) * scale));
  }

  get previewHeightPx(): number | null {
    if (this.editing.pageSize !== 'die-cut' && this.editing.printerType !== 'zebra') return null;
    return Math.max(30, this.editing.labelHeight * this.previewScale);
  }

  /**
   * Actual barcode printing is now always a fixed 50mm×25mm ZPL label
   * (see LabelPrintService) regardless of the layout being edited here,
   * so this just sends a sample of that fixed label to test connectivity.
   */
  async printSample(): Promise<void> {
    const clientName = await this.printSvc.resolveClientName();
    this.printSvc.print('Sample Book Title', [1], clientName);
  }

  isV(o?: FieldOrientation): boolean { return o === 'VT' || o === 'VB'; }

  get previewHasLeft(): boolean {
    const e = this.editing;
    return (e.showTitle   && this.isV(e.titleOrientation)   && (e.titleLocation   || 'left') === 'left') ||
           (e.showAuthor  && this.isV(e.authorOrientation)  && (e.authorLocation  || 'left') === 'left') ||
           (e.showLibrary && this.isV(e.libraryOrientation) && (e.libraryLocation || 'left') === 'left') ||
           (this.isV(e.barcodeOrientation)                  && (e.barcodeLocation || 'left') === 'left');
  }

  get previewHasRight(): boolean {
    const e = this.editing;
    return (e.showTitle   && this.isV(e.titleOrientation)   && e.titleLocation   === 'right') ||
           (e.showAuthor  && this.isV(e.authorOrientation)  && e.authorLocation  === 'right') ||
           (e.showLibrary && this.isV(e.libraryOrientation) && e.libraryLocation === 'right') ||
           (this.isV(e.barcodeOrientation)                  && e.barcodeLocation === 'right');
  }

  getOrientStyle(o?: FieldOrientation): Record<string, string> {
    if (o === 'VT') return { 'writing-mode': 'vertical-rl', 'text-orientation': 'mixed', 'white-space': 'nowrap', 'max-width': 'none' };
    if (o === 'VB') return { 'writing-mode': 'vertical-rl', 'text-orientation': 'mixed', 'transform': 'rotate(180deg)', 'display': 'inline-block', 'white-space': 'nowrap', 'max-width': 'none' };
    return {};
  }

  getLcpBarcodeStyle(o?: FieldOrientation): Record<string, string> {
    if (o === 'VT' || o === 'VB') {
      return {
        'width':      '14px',
        'height':     '72%',
        'background': 'repeating-linear-gradient(0deg, #000 0px, #000 2px, #fff 2px, #fff 3px, #000 3px, #000 4px, #fff 4px, #fff 6px, #000 6px, #000 7px, #fff 7px, #fff 9px)',
        'margin':     '0 4px',
        'flex-shrink': '0',
      };
    }
    return {};
  }

  /** Switch to Zebra mode and lock page type to die-cut. */
  onSetZebra(): void {
    this.editing.printerType = 'zebra';
    this.editing.pageSize    = 'die-cut';
  }

  constructor(
    private api:       ApiService,
    private auth:      AuthService,
    private centerSvc: CenterService,
    private printSvc:  LabelPrintService,
    private pageTitle: PageTitleService,
  ) {}

  ngOnInit(): void {
    this.pageTitle.set('Label Settings', 'Configure barcode label layouts');
    this.loadLayouts();
    this.loadNameSource();
  }

  loadNameSource(): void {
    this.api.getLabelNameSource().subscribe({
      next: (data) => {
        this.labelNameSource     = (data?.source as any) || 'center';
        this.labelNameCustomText = data?.customText || '';
      },
      error: () => {},
    });
  }

  resolveLabelName(): string {
    if (this.labelNameSource === 'licensee') return this.auth.getCurrentUser()?.clientName || '';
    if (this.labelNameSource === 'custom')   return this.labelNameCustomText.trim();
    return this.centerSvc.selectedCenter?.centerName || '';
  }

  loadLayouts(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.api.getLabelLayouts().subscribe({
      next: (data: any[]) => {
        this.layouts = data || [];
        if (this.layouts.length > 0) {
          const defaultIdx = this.layouts.findIndex(l => l.isDefault);
          this.selectLayout(defaultIdx >= 0 ? defaultIdx : 0);
        } else {
          this.startNew();
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to load layouts.';
        this.isLoading = false;
      }
    });
  }

  onLayoutSelect(val: number): void {
    const idx = Number(val);
    if (idx === -1) this.startNew(); else this.selectLayout(idx);
  }

  selectLayout(idx: number): void {
    this.activeIndex   = idx;
    this.isNew         = false;
    this.editing       = { ...LABEL_LAYOUT_DEFAULTS, ...this.layouts[idx] };
    this.savedSnapshot = JSON.stringify(this.editing);
    this.nameError     = '';
    this.errorMessage  = '';
    this.successMessage = '';
  }

  startNew(): void {
    this.isNew         = true;
    this.activeIndex   = -1;
    this.editing       = { name: '', ...LABEL_LAYOUT_DEFAULTS };
    this.markAsDefault = false;
    this.savedSnapshot = '__new__';
    this.nameError     = '';
    this.errorMessage  = '';
    this.successMessage = '';
  }

  applyPreset(preset: { label: string; cfg: Partial<Omit<LabelLayout, 'name'>> }): void {
    this.editing = { ...this.editing, ...preset.cfg };
  }

  private validateName(): boolean {
    const name = this.editing.name.trim();
    if (!name) { this.nameError = 'Layout name is required.'; return false; }
    const duplicate = this.layouts.some((l, i) =>
      l.name.toLowerCase() === name.toLowerCase() && i !== this.activeIndex
    );
    if (duplicate) { this.nameError = 'A layout with this name already exists.'; return false; }
    this.nameError = '';
    return true;
  }

  saveLayout(): void {
    if (!this.validateName()) return;
    this.errorMessage = '';
    this.successMessage = '';
    this.isSaving = true;

    const updated: LabelLayout = { ...this.editing, name: this.editing.name.trim() };
    let newLayouts: LabelLayout[];
    if (this.isNew) {
      if (this.markAsDefault) {
        newLayouts = [...this.layouts.map(l => ({ ...l, isDefault: false })), { ...updated, isDefault: true }];
      } else {
        newLayouts = [...this.layouts, updated];
      }
    } else {
      newLayouts = this.layouts.map((l, i) => i === this.activeIndex ? updated : l);
    }

    this.api.saveLabelLayouts(newLayouts).subscribe({
      next: (saved: any[]) => {
        this.layouts = saved;
        const newIdx = saved.findIndex(l => l.name === updated.name);
        this.selectLayout(newIdx >= 0 ? newIdx : 0);
        this.successMessage = `Layout "${updated.name}" saved.`;
        this.isSaving = false;
        setTimeout(() => this.successMessage = '', 4000);
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to save layout.';
        this.isSaving = false;
      }
    });
  }

  setAsDefault(): void {
    if (this.isNew || this.isCurrentDefault || this.isSaving) return;
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';
    const newLayouts = this.layouts.map((l, i) => ({ ...l, isDefault: i === this.activeIndex }));
    this.api.saveLabelLayouts(newLayouts).subscribe({
      next: (saved: any[]) => {
        this.layouts = saved;
        const newIdx = saved.findIndex(l => l.isDefault);
        this.selectLayout(newIdx >= 0 ? newIdx : this.activeIndex);
        this.successMessage = 'Default layout updated.';
        this.isSaving = false;
        setTimeout(() => this.successMessage = '', 3000);
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to set default.';
        this.isSaving = false;
      }
    });
  }

  deleteLayout(): void {
    if (!this.canDelete) return;
    if (this.isCurrentDefault) {
      this.pendingDeleteIdx   = this.activeIndex;
      this.newDefaultIdx      = 0;
      this.choosingNewDefault = true;
      this.errorMessage  = '';
      this.successMessage = '';
      return;
    }
    const name = this.layouts[this.activeIndex]?.name;
    if (!confirm(`Delete layout "${name}"? This cannot be undone.`)) return;
    this._doDelete(this.activeIndex, null);
  }

  confirmDeleteWithNewDefault(): void {
    if (this.pendingDeleteIdx < 0) return;
    const deletedName = this.layouts[this.pendingDeleteIdx]?.name;
    this.choosingNewDefault = false;
    const remaining   = this.layouts.filter((_, i) => i !== this.pendingDeleteIdx);
    const withDefault = remaining.map((l, i) => ({ ...l, isDefault: i === this.newDefaultIdx }));
    this._doDelete(this.pendingDeleteIdx, withDefault, deletedName);
    this.pendingDeleteIdx = -1;
  }

  cancelDeleteChoice(): void {
    this.choosingNewDefault = false;
    this.pendingDeleteIdx   = -1;
  }

  private _doDelete(deleteIdx: number, overrideLayouts: LabelLayout[] | null, name?: string): void {
    const layoutName = name ?? this.layouts[deleteIdx]?.name;
    this.isSaving    = true;
    const newLayouts = overrideLayouts ?? this.layouts.filter((_, i) => i !== deleteIdx);
    this.api.saveLabelLayouts(newLayouts).subscribe({
      next: (saved: any[]) => {
        this.layouts = saved;
        const defaultIdx = saved.findIndex(l => l.isDefault);
        this.selectLayout(defaultIdx >= 0 ? defaultIdx : 0);
        this.isSaving       = false;
        this.successMessage = `Layout "${layoutName}" deleted.`;
        setTimeout(() => this.successMessage = '', 3000);
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to delete layout.';
        this.isSaving = false;
      }
    });
  }
}
